//src/pages/UnauthorizedPage.tsx
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Home, LockKeyhole, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function UnauthorizedPage() {
  const navigate = useNavigate();

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }

  function handleGoHome() {
    navigate("/", { replace: true });
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-muted/20 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <header className="flex flex-col gap-4 rounded-2xl border bg-background p-5 shadow-sm md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border bg-destructive/10 text-destructive">
              <ShieldAlert className="h-6 w-6" />
            </div>

            <div className="min-w-0">
              <p className="text-sm font-medium text-destructive">Acesso restrito</p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                Esta página não está liberada para o seu usuário.
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                Seu perfil existe e está autenticado, mas não possui a permissão necessária para visualizar, editar ou administrar este recurso.
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>

            <Button onClick={handleGoHome}>
              <Home className="mr-2 h-4 w-4" />
              Ir para o início
            </Button>
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <Card className="overflow-hidden rounded-2xl shadow-sm">
            <CardContent className="space-y-6 p-6">
              <div className="flex items-center gap-3 border-b pb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <LockKeyhole className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold">Você chegou a uma área protegida</h2>
                  <p className="text-sm text-muted-foreground">
                    Isso normalmente acontece por permissão ausente no grupo de acesso.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border bg-card p-4">
                  <div className="text-sm font-medium">Motivo mais comum</div>
                  <p className="mt-1 text-sm leading-5 text-muted-foreground">
                    Seu grupo de acesso não contém a permissão exigida para esta funcionalidade.
                  </p>
                </div>

                <div className="rounded-xl border bg-card p-4">
                  <div className="text-sm font-medium">Quando isso ocorre</div>
                  <p className="mt-1 text-sm leading-5 text-muted-foreground">
                    Ao abrir um link direto, favorito ou uma rota restrita ao seu perfil.
                  </p>
                </div>

                <div className="rounded-xl border bg-card p-4">
                  <div className="text-sm font-medium">Como resolver</div>
                  <p className="mt-1 text-sm leading-5 text-muted-foreground">
                    Solicite ao administrador da empresa a revisão do seu grupo de acesso.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
                <div className="text-sm font-medium text-foreground">Dica</div>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  Volte ao menu principal e acesse apenas as opções disponíveis para o seu perfil. Quando um botão aparecer desabilitado, significa que seu acesso permite consulta, mas não alteração.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm">
            <CardContent className="space-y-4 p-6">
              <h2 className="font-semibold">O que você pode fazer agora</h2>

              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium">1. Voltar</div>
                <p className="mt-1 text-sm leading-5 text-muted-foreground">
                  Retorne para a tela anterior e continue navegando normalmente.
                </p>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium">2. Ir para o início</div>
                <p className="mt-1 text-sm leading-5 text-muted-foreground">
                  Acesse a home do sistema para encontrar módulos liberados para você.
                </p>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium">3. Solicitar acesso</div>
                <p className="mt-1 text-sm leading-5 text-muted-foreground">
                  Peça ao administrador a liberação da permissão necessária.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
